import { PUBLIC_ACCOUNT_URL, PUBLIC_SEGMENT_WRITE_KEY } from '$env/static/public';
import type { Stripe } from 'stripe';
import { addressOfSession } from '../utils/tracking/utils';
import { convertToTrackableItem, getTrackingAmounts } from '$lib/utils/stripe.utils';
import { inDollars } from '$lib/utils/money';
import { isActiveProduct } from '$lib/api/stripe';
import { getPromoOffAmount } from '../utils/stripe';
import type { UserTraits } from '@segment/analytics-node';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createPinMetaTrialRevenuePlugin } from './plugins/pinMetaTrialRevenue';
import { createCommonContextPlugin } from './plugins/commonContext';
import { dev } from '$app/environment';
import { geolocation } from '$lib/state/geolocation.svelte';
import log from '../utils/log';
import { pushToDataLayer } from '$lib/utils/gtm';
import { hashEmail } from '$lib/utils/email';

const analytics = AnalyticsBrowser.load({ writeKey: PUBLIC_SEGMENT_WRITE_KEY });

let trackingInitialized = false;

const trackingQueue: Array<() => Promise<void>> = [];

async function flushQueue() {
	while (trackingQueue.length > 0) {
		if (dev) {
			log.scope('Segment').debug('Flushing...', { count: trackingQueue.length });
		}

		const trackingFn = trackingQueue.shift();
		if (trackingFn) {
			await trackingFn();
		}

		if (dev) {
			log.scope('Segment').debug('Flushed.', { count: trackingQueue.length });
		}
	}
}

export async function initAnalytics(anonymousId: string) {
	await Promise.all([
		analytics.setAnonymousId(anonymousId),
		analytics.register(createCommonContextPlugin(), createPinMetaTrialRevenuePlugin())
	]);

	trackingInitialized = true;

	if (dev) {
		log.scope('Segment').debug('Initialized. Flushing queue...', { anonymousId });
	}

	await flushQueue();
}

function track(trackingFn: () => Promise<void>) {
	if (!trackingInitialized) {
		trackingQueue.push(trackingFn);

		if (dev) {
			log.scope('Segment').debug('Queueing...', { count: trackingQueue.length });
		}

		return Promise.resolve();
	}

	try {
		return trackingFn();
	} catch (error) {
		log.scope('Segment').exception(error);
		return Promise.reject(error);
	}
}

export async function getAnonymousId() {
	const user = await analytics.user();

	const anonId = user.anonymousId();

	if (!anonId) {
		throw new Error('Anonymous ID not found');
	}

	return anonId;
}

export async function trackPage(url: URL) {
	return track(async () => {
		await analytics.page({
			search: url.searchParams.toString()
		});

		if (dev) {
			log.scope('Segment').info(`Page View "${url.pathname}"`);
		}
	});
}

export async function trackEvent<Data extends Record<string, unknown>>(
	eventName: string,
	data: Data = {} as Data
) {
	return track(async () => {
		const eventData = {
			...data,
			geolocation: geolocation.data
		};

		try {
			await analytics.track(eventName, eventData);
		} catch (error) {
			log.scope('Segment').exception(error);
		}

		if (dev) {
			console.groupCollapsed(`[Segment] ${eventName}`);
			console.table(eventData);
			console.groupEnd();
		}
	});
}

export async function identify(user: UserTraits) {
	return track(async () => {
		try {
			await analytics.identify(user);
		} catch (error) {
			log.scope('Segment').exception(error);
		}

		if (dev) {
			console.groupCollapsed('[Segment] Identify');
			console.table(user);
			console.groupEnd();
		}
	});
}

export async function trackAddToCart(
	price: Stripe.Price,
	promotionCode?: Stripe.PromotionCode
): Promise<void> {
	const segmentProduct = getSegmentProduct(price, promotionCode);
	if (!segmentProduct) {
		log.scope('Segment').warn('Cannot find Segment product');
		return;
	}

	pushToDataLayer({
		order: {
			quantity: segmentProduct.quantity,
			couponCode: segmentProduct.coupon,
			total: segmentProduct.price,
		}
	});

	await trackEvent('Product Added', segmentProduct);
}

export async function trackInitiateCheckout(
	checkoutSession: Stripe.Checkout.Session,
	promotionCode?: Stripe.PromotionCode
): Promise<void> {
	const email = checkoutSession.customer_details?.email ?? '';
	const hashedEmail = await hashEmail(email);

	pushToDataLayer({
		order: {
			quantity: checkoutSession.line_items?.data.reduce((acc, item) => acc + (item.quantity ?? 0), 0),
			couponCode: promotionCode?.code,
			total: inDollars(checkoutSession.amount_total || 0),
			id: checkoutSession.id,
			type: checkoutSession.mode === 'subscription' ? 'rental' : 'purchase',
			currency: 'USD',
			email,
			hashed_email: hashedEmail
		}
	});

	await trackEvent('Checkout Started', {
		order_id: checkoutSession.id,
		affiliation: 'Marketing Website',
		value: inDollars(checkoutSession.amount_total || 0),
		discount: promotionCode?.coupon
			? inDollars(getPromoOffAmount(checkoutSession.amount_total || 0, promotionCode?.coupon))
			: 0,
		coupon: promotionCode?.code || '',
		currency: checkoutSession.currency,
		products: checkoutSession.line_items?.data
			.map((sessionItem) => getSegmentProduct(sessionItem.price, promotionCode) ?? null)
			.filter(Boolean),
		type: checkoutSession.mode === 'subscription' ? 'rental' : 'purchase'
	});
}

export async function trackLeadGenerated(name: string, category: string, value: string) {
	// commented out because of pending discussion
	await trackEvent('Lead Generated', {
		// old
		//content_category: category,
		//content_name: name,
		// new (based on https://docs.google.com/document/d/1IUYq2d8lq3WJOo_BkVEYfSn2ngCBVsBO3rjTAkYnV9E/edit)
		product_group: category,
		product: name,
		value: value,
		currency: 'USD'
	});
}

function getSegmentProduct(
	price: Stripe.Price | null | undefined,
	promotionCode?: Stripe.PromotionCode
) {
	if (!price || !isActiveProduct(price.product)) {
		log.scope('Segment').error('Can not find price or active product', { price });
		return;
	}

	return {
		product_id: price.product.id,
		sku: price.metadata._product_sku ?? '',
		name: price.product.name,
		price: inDollars(price.unit_amount ?? 0),
		quantity: 1,
		coupon: promotionCode?.code,
		position: 1
	};
}

function getPurchaseValue(session: Stripe.Checkout.Session) {
	const RENTAL_LTV = 94100;
	const AFFIRM_FEE = 0.0799;
	const AFTERPAY_FEE = 0.063;

	let purchaseValue = 0;

	for (const item of session.line_items?.data ?? []) {
		const price = item.price;

		if (!price?.unit_amount) continue;

		if (price.recurring?.interval === 'month') {
			purchaseValue += RENTAL_LTV;
		} else {
			if (!session.payment_intent || typeof session.payment_intent === 'string') {
				log.scope('Segment').warn('No payment intent found', { session });
				continue;
			}

			let paymentFeeFactor = 1.0;

			if (session.payment_intent.payment_method_types.includes('affirm')) {
				paymentFeeFactor -= AFFIRM_FEE;
			} else if (session.payment_intent.payment_method_types.includes('afterpay_clearpay')) {
				paymentFeeFactor -= AFTERPAY_FEE;
			}

			purchaseValue += (price.unit_amount - item.amount_discount) * paymentFeeFactor;
		}
	}

	return inDollars(purchaseValue);
}

// try to match:
// https://github.com/chewielabs/ChewieWebAccount/blob/main/www/src/utils/segment.js
export async function trackOrderComplete(session: Stripe.Checkout.Session) {
	const amounts = getTrackingAmounts(session);

	await trackEvent(
		session.metadata?.is_for_trial === 'true' ? 'Trial Started' : 'Order Completed',
		{
			subtotal: amounts.dollars.subtotalAfterDiscountsAndBeforeTaxes,
			total: amounts.dollars.total,
			revenue: getPurchaseValue(session),
			shipping: amounts.dollars.shipping,
			tax: amounts.dollars.tax,
			discount: amounts.dollars.discount,
			affiliation: 'Stripe',
			order_id: session.id,
			currency: session.currency?.toUpperCase() || 'USD',
			products: session.line_items?.data?.map(convertToTrackableItem),
			chewie_web_account_url: PUBLIC_ACCOUNT_URL,
			order_date_created: new Date(session.created * 1000).toISOString(),
			order_number: session.id,
			shipping_address: addressOfSession(session),
			referral_code: session.metadata?.referral_code,
			is_trial: session.metadata?.is_for_trial === 'true',
			user_labels: session.metadata?.user_labels
		}
	);
}

